import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';

import { CSVColumn, CSVParseResult, humanizeCsvHeader } from '~utils/csv/csvProcessor';

// Preview is a sanity check, not a data grid; cap it so huge files stay snappy
const PREVIEW_ROW_LIMIT = 20;

const ERROR_LIST_LIMIT = 10;

interface CSVParsePreviewProps {
  columns: CSVColumn[];
  parseResult: CSVParseResult;
  sx?: SxProps<Theme>;
}

export function CSVParsePreview({ columns, parseResult, sx }: CSVParsePreviewProps) {
  const { errors, validRows } = parseResult;

  // Scoped to the rendered rows and memoized: the parent re-renders once per imported
  // row while an import runs, and parseResult keeps every valid row live.
  const previewRows = useMemo(() => validRows.slice(0, PREVIEW_ROW_LIMIT), [validRows]);

  // Only show columns the file actually uses, so narrow files get a narrow preview.
  const previewColumns = useMemo(
    () => columns.filter((column) => previewRows.some((row) => row[column.key]?.trim())),
    [columns, previewRows],
  );

  return (
    <Box sx={sx}>
      {validRows.length > 0 && (
        <TableContainer
          sx={{
            maxHeight: 260,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {previewColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    // Matches the data grid column header type (12px / 600)
                    sx={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: 12 }}
                  >
                    {column.label ?? humanizeCsvHeader(column.header)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((row, rowIdx) => (
                <TableRow key={rowIdx}>
                  {previewColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{ whiteSpace: 'nowrap', fontSize: 12 }}
                    >
                      {row[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {validRows.length > PREVIEW_ROW_LIMIT && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {t('csv_import_export.preview_more_rows', {
            count: validRows.length - PREVIEW_ROW_LIMIT,
          })}
        </Typography>
      )}

      {errors.length > 0 && (
        <Box
          sx={{
            maxHeight: 150,
            overflow: 'auto',
            border: 1,
            borderColor: 'error.light',
            borderRadius: 1,
            p: 1,
            mb: 2,
            backgroundColor: 'error.lighter',
          }}
        >
          {errors.slice(0, ERROR_LIST_LIMIT).map((error, idx) => (
            <Typography
              key={idx}
              variant="body2"
              sx={{
                color: 'error.main',
                fontSize: 12,
              }}
            >
              {t('csv_import_export.row_error', {
                row: t('csv_import_export.row_label', { row: error.row }),
                message: error.message,
              })}
            </Typography>
          ))}
          {errors.length > ERROR_LIST_LIMIT && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: 12,
              }}
            >
              {t('csv_import_export.more_errors', {
                count: errors.length - ERROR_LIST_LIMIT,
              })}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
